// React
import CircularProgress from "@material-ui/core/CircularProgress";
// Components
import Fade from "@material-ui/core/Fade";
import Icon from "@material-ui/core/Icon";
// MuiTheme
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import frCH from "date-fns/esm/locale/fr-CH";
import FilePondPluginImageCrop from "filepond-plugin-image-crop";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImageResize from "filepond-plugin-image-resize";
import FilePondPluginImageTransform from "filepond-plugin-image-transform";
// Import FilePond styles
import "filepond/dist/filepond.min.css";
import createBrowserHistory from "history/createBrowserHistory";
import DateFnsUtils from "material-ui-pickers/utils/date-fns-utils";
// Pickers
import MuiPickersUtilsProvider from "material-ui-pickers/utils/MuiPickersUtilsProvider";
import * as React from "react";
// Import React FilePond
import { registerPlugin } from "react-filepond";
// Redux
import { connect } from "react-redux";
// Router
import { Redirect, Route, Router as Router } from "react-router-dom";
import AppBarTop from "./components/AppBarTop";
import Navigation from "./components/Navigation";
import Notification from "./components/Notification";
// Functions
import { getURLByEnv } from "./framework/utils/URL";
import { NetworkManager } from "./managers/NetworkManager";
// MainStore
import { MainStore } from "./stores/MainStore";
import NewsProfile from "./views/ArticleProfile";
import News from "./views/Articles";
import Home from "./views/Home";
// Views
import Login from "./views/Login";
import RealizationProfile from "./views/RealizationProfile";
import Realizations from "./views/Realizations";
import Supplier from "./views/Supplier";
import SupplierProfile from "./views/SupplierProfile";
import Comite from "./views/Team";
import ComiteProfile from "./views/TeamProfile";
import Texts from "./views/Texts";
import TextsProfile from "./views/TextsProfile";

// Register the plugin
registerPlugin(
  FilePondPluginImageExifOrientation,
  FilePondPluginImageCrop,
  FilePondPluginImageResize,
  FilePondPluginImageTransform
);

interface State {
  open: boolean;
}

interface Props {
  navigation: any;
  notification: any;
  overlay: any;
  user: any;
  history: any;
}

const theme = createMuiTheme({
  palette: {
    primary: {
      contrastText: "#fff",
      dark: "#0D47A1",
      light: "#90CAF9",
      main: "#1976D2"
    },
    secondary: {
      contrastText: "#000",
      dark: "#ba000d",
      light: "#ff7961",
      main: "#f44336"
    }
  }
});

const history = createBrowserHistory();

const PrivateRoute = ({ component: Component, isConnected, ...rest }: any) => (
  <Route
    {...rest}
    render={(props) =>
      isConnected ? (
        <Component {...props} />
      ) : (
        <Redirect to={`${getURLByEnv()}/Connexion`} />
      )
    }
  />
);

/**
 * @description React App
 * @author Lucas Fridez <lucas@fridez.dev>
 * @export
 * @class App
 * @extends {React.Component}
 */
export default connect((state) => state)(
  class App extends React.Component<Props, State> {
    /**
     * Creates an instance of App.
     * @author Lucas Fridez <lucas@fridez.dev>
     * @param {*} props
     * @memberof App
     */
    constructor(props: any) {
      super(props);
    }

    componentWillMount() {
      if (NetworkManager.getUserCredentials() !== undefined) {
        MainStore.loginUser(NetworkManager.getUserCredentials());
      }
    }

    /**
     * @description Render the WebApp in the Browser
     * @author Lucas Fridez <lucas@fridez.dev>
     * @returns
     * @memberof App
     */
    public render() {
      return (
        <Router history={history}>
          <MuiThemeProvider theme={theme}>
            <MuiPickersUtilsProvider utils={DateFnsUtils} locale={frCH}>
              <div className="App">
                <Navigation />
                <div
                  className={
                    this.props.navigation.navigation
                      ? "container-reduced"
                      : "container"
                  }
                >
                  <AppBarTop />
                  {this.props.navigation.backShown && (
                    <span
                      className="back-btn mouse-hover"
                      onClick={() => {
                        MainStore.goBack(history.location.pathname);
                        history.goBack();
                      }}
                    >
                      <Icon>keyboard_backspace</Icon>
                      <Typography variant="subheading">Retour</Typography>
                    </span>
                  )}
                  <div role="main">
                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/`}
                      component={Home}
                    />
                    <Route
                      exact={true}
                      path={`${getURLByEnv()}/Connexion`}
                      component={Login}
                    />

                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Realisations`}
                      component={Realizations}
                    />
                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Realisations/Créer`}
                      component={RealizationProfile}
                    />
                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Realisations/Modifier/:id`}
                      component={RealizationProfile}
                    />

                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Equipe`}
                      component={Comite}
                    />
                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Equipe/Créer`}
                      component={ComiteProfile}
                    />
                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Equipe/Modifier/:id`}
                      component={ComiteProfile}
                    />

                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Textes`}
                      component={Texts}
                    />
                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Textes/Créer`}
                      component={TextsProfile}
                    />
                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Textes/Modifier/:id`}
                      component={TextsProfile}
                    />

                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Articles`}
                      component={News}
                    />
                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Articles/Créer`}
                      component={NewsProfile}
                    />
                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Articles/Modifier/:id`}
                      component={NewsProfile}
                    />

                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Fournisseurs`}
                      component={Supplier}
                    />
                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Fournisseurs/Créer`}
                      component={SupplierProfile}
                    />
                    <PrivateRoute
                      isConnected={this.props.user.connected}
                      exact={true}
                      path={`${getURLByEnv()}/Fournisseurs/Modifier/:id`}
                      component={SupplierProfile}
                    />
                  </div>
                </div>
                {this.props.overlay.overlay.show && (
                  <Fade in={true}>
                    <div id="overlay">
                      <CircularProgress
                        color="primary"
                        thickness={3}
                        size={75}
                      />
                      <Typography color="primary" variant="body1">
                        {this.props.overlay.overlay.text}
                      </Typography>
                    </div>
                  </Fade>
                )}
                <Notification
                  className={this.props.notification.notification.className}
                  open={this.props.notification.notification.show}
                  text={this.props.notification.notification.text}
                />
              </div>
            </MuiPickersUtilsProvider>
          </MuiThemeProvider>
        </Router>
      );
    }
  }
);



// WEBPACK FOOTER //
// ./src/app/App.tsx